/**
 * BilgiKategorileriPage.jsx — Personel Bilgi Kategorileri (inceleme/admin tarafı)
 * Route: yok — #385'ten beri Kişi Kuralları sayfasının "Kişi Künye Bilgileri" sekmesi (/Management/Kisi/Kurallar?tab=kunye,
 * pages/Management/Kisi/KisiKurallariPage.jsx); eski /Management/Kisi/BilgiKategorileri adresi oraya yönlenir.
 *
 * "Her işçinin kartına yeni bir alan eklemek bir kolon + bir deploy değil, bir POST olsun."
 * Kişi kartında sorulacak ekstra alanları burada tanımlıyoruz, değerleri kişi detay
 * sayfası topluyor.
 *
 * Servis: services/kisiAdminService.js
 * Rehber: docs/guides/FE-PersonnelController-kimlik-meslek-guide.md §6
 *
 * Sayfanın taşıdığı üç kural:
 *  1. **SİLME, EMEKLİYE AYIR.** `aktif: false` alanı formdan kaldırır ama toplanmış her
 *     değeri saklar ve o değere sahip kişilerde dönmeye devam eder. `DELETE` yalnızca
 *     hiç kullanılmamışken çalışır (aksi halde 409). Bir soru önemsizleşebilir, ama
 *     cevaplar yok olmaz.
 *  2. **`kod` değiştirilemez.** Değerler, dışa aktarımlar ve form anahtarları ona asılı;
 *     bu yüzden düzenleme formunda salt-okunur ve PUT gövdesine hiç konmuyor.
 *  3. **`zorunlu` hiçbir şeyi engellemez.** Yalnızca forma "bunu sor" der; giriş kartını
 *     asla bloklamaz. Etiket bunu açıkça söylüyor ki kimse buraya bir kapı sanmasın.
 *
 * 2026-09-04: sayfa artık önce YERLEŞİK ALANLARI gösteriyor (`person` tablosunun kendi
 * kolonları — telefon, uyruk, KVKK durumu, ...). Katalog boş başladığı için
 * ekran "kişi hakkında hiçbir şey sorulmuyor" izlenimi veriyordu; oysa kart bunları hep
 * sordu. Tablo salt-okunur.
 *
 * ⚠️ KVKK (kanun, belge değil): buradan tanımlanan serbest metin bir alan "sağlık durumu"
 * da olabilir ve backend bunu "yaka rengi"nden ayırt edemez. Uyarı formun içinde duruyor;
 * neyin sorulabileceğine uyumdan sorumlu kişi karar verir.
 */
import React, { useEffect, useState } from "react";
import {
  Card, CardBody, Row, Col, Button, Badge, Spinner, Alert, Input, Label,
  Modal, ModalHeader, ModalBody, ModalFooter, Table,
} from "reactstrap";
import { Plus, Save, Trash2, EyeOff, Eye, ShieldAlert, Info, ListPlus, X, Database } from "lucide-react";
import { PageFrame, PageBody } from "../../../Components/Common/EmbeddablePage";
import {
  getBilgiTipleri, createBilgiTipi, updateBilgiTipi, setBilgiTipiAktif, deleteBilgiTipi,
  isStaffRefusal, VERI_TIPLERI,
} from "../../../services/kisiAdminService";
import { useTranslation } from "react-i18next";
import i18n from "../../../i18n";

const EMPTY_FORM = {
  kod: "", ad: "", aciklama: "", veriTipi: "METIN", secenekler: [],
  zorunlu: false, firmaGirebilir: true, fuarBazli: false, siralama: 0, aktif: true,
};

// `kod` deseni backend ile birebir: ^[a-z][a-z0-9_]{1,49}$ (2–50 karakter, ilki harf).
const KOD_REGEX = /^[a-z][a-z0-9_]{1,49}$/;

/**
 * YERLEŞİK ALANLAR — kişi kartının `person` tablosunda ZATEN var olan, bu katalogdan
 * ÖNCE gelen alanlar. Katalog boş başladığı için ekran "hiçbir şey sorulmuyor" gibi
 * görünüyordu; oysa kart bunları hep sordu. Buradaki liste kaynağın kendisidir:
 * `GET /personnel/kartduzenle/{personId}` → `person` (ham kolon adları).
 *
 * ⚠️ Hiçbiri buradan düzenlenemez — kolonlar, katalog satırları değil. Katalog bunların
 * YERİNE geçmiyor, yanlarına yeni alan eklemeyi bir POST'a indiriyor.
 */
const YERLESIK_ALANLAR = [
  // #367: künye alanları Kişi Detayı başlığındaki "Düzenle ve Gör" penceresinden doldurulur (eski Detaylar sekmesi kalktı).
  { kolon: "person_id_number", get ad() { return i18n.t("yonetim:bilgiKategorileri.tCPasaportNo"); }, tip: "METIN", get nerede() { return i18n.t("yonetim:bilgiKategorileri.kisiDetayiDuzenleVeGor"); } },
  { kolon: "person_name", get ad() { return i18n.t("yonetim:bilgiKategorileri.ad"); }, tip: "METIN", get nerede() { return i18n.t("yonetim:bilgiKategorileri.kisiDetayiDuzenleVeGor"); } },
  { kolon: "person_surname", get ad() { return i18n.t("yonetim:bilgiKategorileri.soyad"); }, tip: "METIN", get nerede() { return i18n.t("yonetim:bilgiKategorileri.kisiDetayiDuzenleVeGor"); } },
  { kolon: "person_nationality_id", get ad() { return i18n.t("yonetim:bilgiKategorileri.uyruk"); }, tip: "SECIM", get nerede() { return i18n.t("yonetim:bilgiKategorileri.kisiDetayiDuzenleVeGor2"); }, get secenekler() { return [i18n.t("yonetim:bilgiKategorileri.secenek.tc"), i18n.t("yonetim:bilgiKategorileri.secenek.yabanci")]; } }, // #349 WAS: secenekler: ["T.C.", "Yabancı"]
  { kolon: "person_phone", get ad() { return i18n.t("yonetim:bilgiKategorileri.telefon"); }, tip: "METIN", get nerede() { return i18n.t("yonetim:bilgiKategorileri.kisiDetayiDuzenleVeGor2"); } },
  { kolon: "person_email", ad: "E-posta", tip: "METIN", get nerede() { return i18n.t("yonetim:bilgiKategorileri.kisiDetayiDuzenleVeGor2"); } },
  { kolon: "meslek_id", get ad() { return i18n.t("yonetim:bilgiKategorileri.meslek"); }, tip: "SECIM", get nerede() { return i18n.t("yonetim:bilgiKategorileri.kisiDetayiIsgBelgeleriMeslek"); } },
  { kolon: "person_logo", get ad() { return i18n.t("yonetim:bilgiKategorileri.fotografVesikalik"); }, tip: "METIN", get nerede() { return i18n.t("yonetim:bilgiKategorileri.kisiDetayiDuzenleVeGor3"); } },
  { kolon: "person_kvkk_status_id", get ad() { return i18n.t("yonetim:bilgiKategorileri.kvkkDurumu"); }, tip: "SECIM", get nerede() { return i18n.t("yonetim:bilgiKategorileri.kisiDetayiKontroller"); }, get secenekler() { return [i18n.t("yonetim:bilgiKategorileri.secenek.yok"), i18n.t("yonetim:bilgiKategorileri.secenek.onaylandi"), i18n.t("yonetim:bilgiKategorileri.secenek.reddedildi"), i18n.t("yonetim:bilgiKategorileri.secenek.islakImza")]; } }, // #349 WAS: secenekler: ["Yok", "Onaylandı", "Reddedildi", "Islak imza teslim"]
  { kolon: "person_kimlik_dogrulama_durum", get ad() { return i18n.t("yonetim:bilgiKategorileri.kimlikDogrulama"); }, tip: "SECIM", get nerede() { return i18n.t("yonetim:bilgiKategorileri.kimlikDogrulamaSayfasiKisiDetayi"); }, get secenekler() { return [i18n.t("yonetim:bilgiKategorileri.secenek.dogrulanmadi"), i18n.t("yonetim:bilgiKategorileri.secenek.onaylandi"), i18n.t("yonetim:bilgiKategorileri.secenek.reddedildi")]; } }, // #349 WAS: secenekler: ["Doğrulanmadı", "Onaylandı", "Reddedildi"]
  // #368: KKD (eski adı Yelek), yasak ve yasak notu Kişi Detayı › Kontroller sekmesinden düzenlenir; Kişi Listesi
  // yalnızca gösterir (KKD karesi, YASAKLI yazısı). Kolon adı `person_given_vest` aynı kalır.
  { kolon: "person_given_vest", get ad() { return i18n.t("yonetim:bilgiKategorileri.kkdVerildi"); }, tip: "EVET_HAYIR", get nerede() { return i18n.t("yonetim:bilgiKategorileri.kisiDetayiKontrollerKisiListesi"); } },
  // #370: İşveren işareti artık firma başınadır (Kişi Detayı › Firmalar › İşveren sütunu); bu kolon kişi düzeyindeki eski bayrak.
  { kolon: "person_is_owner", get ad() { return i18n.t("yonetim:bilgiKategorileri.isverenMi"); }, tip: "EVET_HAYIR", get nerede() { return i18n.t("yonetim:bilgiKategorileri.kisiDetayiFirmalar"); } },
  { kolon: "person_is_forbidden", get ad() { return i18n.t("yonetim:bilgiKategorileri.girisiYasakli"); }, tip: "EVET_HAYIR", get nerede() { return i18n.t("yonetim:bilgiKategorileri.kisiDetayiKontrollerKisiListesi"); } },
  { kolon: "person_forbidden_note", get ad() { return i18n.t("yonetim:bilgiKategorileri.yasakNotu"); }, tip: "METIN", get nerede() { return i18n.t("yonetim:bilgiKategorileri.kisiDetayiKontroller"); } },
];

function veriTipiLabel(value) {
  return VERI_TIPLERI.find((t) => t.value === value)?.label ?? value;
}

export default function BilgiKategorileriPage({ embedded = false }) {
  const { t: tx } = useTranslation("yonetim");
  const [rows, setRows] = useState([]);
  const [showRetired, setShowRetired] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [infoMsg, setInfoMsg] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [editing, setEditing] = useState(null); // null | {} (yeni) | satır (düzenleme)
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [busy, setBusy] = useState(false);
  const [yerlesikAcik, setYerlesikAcik] = useState(true);

  // Gömülüyken başlığı Kişi Kuralları sayfası koyar (#385).
  useEffect(() => { if (!embedded) document.title = i18n.t("yonetim:bilgiKategorileri.personelBilgiKategorileriFds"); }, [embedded]);

  useEffect(() => {
    let ignore = false;
    async function run() {
      setLoading(true);
      setError(null);
      try {
        // Yönetim ekranı emekliye ayrılanları da görmeli — varsayılan `onlyActive=true`
        // gizler, bu yüzden burada bilinçli olarak false gönderiyoruz.
        const data = await getBilgiTipleri({ onlyActive: !showRetired });
        if (!ignore) setRows(data);
      } catch (e) {
        if (!ignore) setError(e.message);
      } finally {
        if (!ignore) setLoading(false);
      }
    }
    run();
    return () => { ignore = true; };
  }, [showRetired, reloadToken]);

  function showInfo(msg) {
    setInfoMsg(msg);
    setTimeout(() => setInfoMsg(null), 4000);
  }

  async function toggleAktif(row) {
    setBusy(true);
    setError(null);
    try {
      await setBilgiTipiAktif(row.bilgiTipiId, !row.aktif);
      showInfo(row.aktif
        ? tx("bilgiKategorileri.emekliyeAyrildiFormArtikSormaz", { ad: row.ad })
        : tx("bilgiKategorileri.tekrarAktif", { ad: row.ad }));
      setReloadToken((t) => t + 1);
    } catch (e) {
      setError(staffAwareMessage(e));
    } finally {
      setBusy(false);
    }
  }

  async function confirmDelete() {
    setBusy(true);
    setError(null);
    try {
      await deleteBilgiTipi(deleteTarget.bilgiTipiId);
      showInfo(tx("bilgiKategorileri.silindi", { ad: deleteTarget.ad }));
      setDeleteTarget(null);
      setReloadToken((t) => t + 1);
    } catch (e) {
      // ⚠️ 409 hem "hâlâ kullanımda" hem "böyle bir kategori yok" için dönüyor — ayrımı
      // mesaj yapar, kod değil.
      setError(staffAwareMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <PageFrame embedded={embedded}>
      <PageBody embedded={embedded} title={tx("bilgiKategorileri.personelBilgiKategorileri")} pageTitle={tx("bilgiKategorileri.kisi")}>

        <Card className="mb-3">
          <CardBody>
            <Row className="g-3 align-items-center">
              <Col>
                <div className="small text-muted">
                  {tx("bilgiKategorileri.kisiKartindaSorulacakEkstraAlanlar2")}{" "}<strong>{tx("bilgiKategorileri.buAlanlarinHicbiriGirisKartini")}</strong>{" "}{tx("bilgiKategorileri.zorunluYalnizcaFormunSoruSormasini")}
                </div>
              </Col>
              <Col md="auto">
                <div className="form-check form-switch">
                  <Input
                    type="checkbox" className="form-check-input" id="showRetired"
                    checked={showRetired} onChange={(e) => setShowRetired(e.target.checked)}
                  />
                  <Label className="form-check-label small" for="showRetired">
                    {tx("bilgiKategorileri.emekliyeAyrilanlariDaGoster")}
                  </Label>
                </div>
              </Col>
              <Col md="auto">
                <Button color="success" size="sm" onClick={() => setEditing({ ...EMPTY_FORM })}>
                  <Plus size={14} className="me-1" />{tx("bilgiKategorileri.yeniKategori")}
                </Button>
              </Col>
            </Row>
          </CardBody>
        </Card>

        {error && <Alert fade={false} color="danger">{error}</Alert>}
        {infoMsg && <Alert fade={false} color="success">{infoMsg}</Alert>}

        {/*
          YERLEŞİK ALANLAR. Katalog boş başladığı için bu sayfa "kişi hakkında hiçbir
          şey sorulmuyor" izlenimi veriyordu. Kart bu alanları hep sordu; katalog
          onların yerine geçmiyor, yanlarına yenisini eklemeyi kolaylaştırıyor.
        */}
        <Card className="mb-3">
          <CardBody className={yerlesikAcik ? "pb-0" : ""}>
            <Row className="g-2 align-items-center">
              <Col>
                <div className="fw-semibold d-flex align-items-center gap-2">
                  <Database size={16} className="text-muted" />
                  {tx("bilgiKategorileri.kisiKartindaZatenBulunanAlanlar")}
                  <Badge color="light" className="text-body border fw-normal">{YERLESIK_ALANLAR.length}{" "}{tx("bilgiKategorileri.yerlesikAlan")}</Badge>
                </div>
                <div className="text-muted small mt-1">
                  {tx("bilgiKategorileri.bunlar")}{" "}<code>{tx("bilgiKategorileri.person")}</code>{" "}{tx("bilgiKategorileri.tablosununKendiKolonlariBuKatalogdan")}{" "}<strong>{tx("bilgiKategorileri.yerine")}</strong>{" "}{tx("bilgiKategorileri.gecmiyorYenisiniEklemeyiBirKolon")}
                </div>
              </Col>
              <Col md="auto">
                <Button color="light" size="sm" onClick={() => setYerlesikAcik((v) => !v)}>
                  {yerlesikAcik ? tx("bilgiKategorileri.gizle") : tx("bilgiKategorileri.goster")}
                </Button>
              </Col>
            </Row>
          </CardBody>
          {yerlesikAcik && (
            <div className="table-responsive">
              <Table size="sm" className="align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th>{tx("bilgiKategorileri.alan")}</th>
                    <th style={{ width: 150 }}>{tx("bilgiKategorileri.veriTipi")}</th>
                    <th style={{ width: 260 }}>{tx("bilgiKategorileri.neredeDoldurulur")}</th>
                  </tr>
                </thead>
                <tbody>
                  {YERLESIK_ALANLAR.map((f) => (
                    <tr key={f.kolon}>
                      <td>
                        <div className="fw-semibold">
                          {f.ad}
                          <Badge color="light" className="ms-2 text-body border fw-normal">{tx("bilgiKategorileri.yerlesik")}</Badge>
                        </div>
                        <div className="text-muted small"><code>{f.kolon}</code></div>
                        {(f.secenekler || []).length > 0 && (
                          <div className="d-flex flex-wrap gap-1 mt-1">
                            {f.secenekler.map((o) => (
                              <Badge key={o} color="light" className="text-body border fw-normal">{o}</Badge>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="small">{veriTipiLabel(f.tip)}</td>
                      <td className="small text-muted">{f.nerede}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card>

        {loading && (
          <div className="text-center py-5">
            <Spinner color="primary" />
            <p className="mt-2">{tx("common:yukleniyor")}</p>
          </div>
        )}

        {!loading && (
          <Card>
            <CardBody className="p-0">
              <div className="table-responsive">
                <Table className="align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: 60 }}>{tx("bilgiKategorileri.sira")}</th>
                      <th>{tx("bilgiKategorileri.alan")}</th>
                      <th style={{ width: 150 }}>{tx("bilgiKategorileri.veriTipi")}</th>
                      <th style={{ width: 190 }}>{tx("bilgiKategorileri.kimGirebilir")}</th>
                      <th style={{ width: 110 }}>{tx("bilgiKategorileri.kullanim")}</th>
                      <th style={{ width: 230 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.length === 0 && (
                      <tr>
                        <td colSpan={6} className="text-center text-muted py-4">
                          {tx("bilgiKategorileri.henuz")}{" "}<strong>{tx("bilgiKategorileri.ek")}</strong>{" "}{tx("bilgiKategorileri.kategoriTanimlanmamisKatalogBosBaslar")}{" "}<strong>{tx("bilgiKategorileri.yerlesikAlanlar")}</strong>{" "}{tx("bilgiKategorileri.tablosunda")}
                        </td>
                      </tr>
                    )}
                    {rows.map((r) => (
                      <tr key={r.bilgiTipiId} className={r.aktif ? "" : "opacity-50"}>
                        <td className="text-muted">{r.siralama}</td>
                        <td>
                          <div className="fw-semibold">
                            {r.ad}
                            {!r.aktif && <Badge color="secondary" className="ms-2 fw-normal">{tx("bilgiKategorileri.emekli")}</Badge>}
                            {r.zorunlu && <Badge color="warning" className="ms-2 fw-normal">{tx("bilgiKategorileri.formdaZorunlu")}</Badge>}
                            {r.fuarBazli && <Badge color="info" className="ms-2 fw-normal">{tx("bilgiKategorileri.fuarBazli")}</Badge>}
                          </div>
                          <div className="text-muted small">
                            <code>{r.kod}</code>
                            {r.aciklama ? ` — ${r.aciklama}` : ""}
                          </div>
                          {r.veriTipi === "SECIM" && (r.secenekler || []).length > 0 && (
                            <div className="d-flex flex-wrap gap-1 mt-1">
                              {r.secenekler.map((s) => (
                                <Badge key={s} color="light" className="text-body border fw-normal">{s}</Badge>
                              ))}
                            </div>
                          )}
                        </td>
                        <td>{veriTipiLabel(r.veriTipi)}</td>
                        <td>
                          {r.firmaGirebilir
                            ? <span className="small">{tx("bilgiKategorileri.firmaVeSakaPersoneli")}</span>
                            : <span className="small text-primary fw-semibold">{tx("bilgiKategorileri.yalnizcaSakaPersoneli")}</span>}
                        </td>
                        <td>
                          <Badge color={r.kullanimSayisi > 0 ? "primary" : "light"} className={r.kullanimSayisi > 0 ? "" : "text-body border"}>
                            {tx("bilgiKategorileri.kisi2", { v: r.kullanimSayisi ?? 0 })}
                          </Badge>
                        </td>
                        <td className="text-end">
                          <Button color="light" size="sm" className="me-1" onClick={() => setEditing(r)}>
                            {tx("common:duzenle")}
                          </Button>
                          <Button
                            color={r.aktif ? "warning" : "success"} size="sm" outline className="me-1"
                            disabled={busy} onClick={() => toggleAktif(r)}
                            title={r.aktif ? tx("bilgiKategorileri.emekliyeAyirDegerlerKorunur") : tx("bilgiKategorileri.tekrarAktifEt")}
                          >
                            {r.aktif ? <EyeOff size={14} /> : <Eye size={14} />}
                          </Button>
                          <Button
                            color="danger" size="sm" outline disabled={busy || (r.kullanimSayisi ?? 0) > 0}
                            onClick={() => setDeleteTarget(r)}
                            title={(r.kullanimSayisi ?? 0) > 0
                              ? tx("bilgiKategorileri.kullanimdaOlanKategoriSilinemezEmekliye")
                              : tx("common:sil")}
                          >
                            <Trash2 size={14} />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </CardBody>
          </Card>
        )}
      </PageBody>

      {editing && (
        <KategoriModal
          key={editing.bilgiTipiId ?? "new"}
          row={editing}
          onClose={() => setEditing(null)}
          onSaved={(msg) => { setEditing(null); showInfo(msg); setReloadToken((t) => t + 1); }}
        />
      )}

      <Modal isOpen={Boolean(deleteTarget)} toggle={() => setDeleteTarget(null)}>
        <ModalHeader toggle={() => setDeleteTarget(null)}>{tx("bilgiKategorileri.kategoriyiSil")}</ModalHeader>
        <ModalBody>
          <p><strong>{deleteTarget?.ad}</strong>{" "}{tx("bilgiKategorileri.kategorisiKaliciOlarakSilinecek")}</p>
          <Alert fade={false} color="warning" className="small d-flex gap-2 mb-0">
            <Info size={16} className="flex-shrink-0" />
            <span>
              {tx("bilgiKategorileri.silmeYalnizcaHicDegerToplanmamisken")}{" "}<strong>{tx("bilgiKategorileri.emekliyeAyirmaktir")}</strong>{" "}{tx("bilgiKategorileri.formSormayiBirakirToplanmisCevaplar")}
            </span>
          </Alert>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setDeleteTarget(null)}>{tx("bilgiKategorileri.vazgec")}</Button>
          <Button color="danger" onClick={confirmDelete} disabled={busy}>
            {busy ? <Spinner size="sm" /> : tx("common:sil")}
          </Button>
        </ModalFooter>
      </Modal>
    </PageFrame>
  );
}

function KategoriModal({ row, onClose, onSaved }) {
  const { t: tx } = useTranslation("yonetim");
  const isNew = !row.bilgiTipiId;
  const [form, setForm] = useState({ ...EMPTY_FORM, ...row, secenekler: row.secenekler || [] });
  const [secenekInput, setSecenekInput] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // `veriTipi`/`fuarBazli`, tek bir değer bile girilmişse backend'de kilitli (400 ile
  // "emekliye ayır ve yeniden oluştur" der) — formda da kilitliyoruz ki kullanıcı boşuna
  // denemesin.
  const tipKilitli = !isNew && (row.kullanimSayisi ?? 0) > 0;

  function setField(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  function addSecenek() {
    const v = secenekInput.trim();
    if (!v) return;
    if (v.includes(";")) { setError(tx("bilgiKategorileri.secenekIceremezDepolamaAyiraciOdur")); return; }
    if (form.secenekler.includes(v)) return;
    setField("secenekler", [...form.secenekler, v]);
    setSecenekInput("");
    setError(null);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.ad.trim()) { setError(tx("bilgiKategorileri.alanAdiZorunludur")); return; }
    if (isNew && !KOD_REGEX.test(form.kod.trim().toLowerCase())) {
      setError(tx("bilgiKategorileri.kod250KarakterOlmali"));
      return;
    }
    if (form.veriTipi === "SECIM" && form.secenekler.length === 0) {
      setError(tx("bilgiKategorileri.secimListesiIcinEnAz"));
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      if (isNew) {
        await createBilgiTipi(form);
        onSaved(tx("bilgiKategorileri.kategorisiOlusturuldu", { ad: form.ad }));
      } else {
        await updateBilgiTipi(row.bilgiTipiId, form);
        onSaved(tx("bilgiKategorileri.guncellendi", { ad: form.ad }));
      }
    } catch (err) {
      setError(staffAwareMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  const tipMeta = VERI_TIPLERI.find((t) => t.value === form.veriTipi);

  return (
    <Modal isOpen toggle={onClose} size="lg">
      <ModalHeader toggle={onClose}>{isNew ? tx("bilgiKategorileri.yeniBilgiKategorisi") : tx("bilgiKategorileri.duzenle", { ad: row.ad })}</ModalHeader>
      <form onSubmit={handleSubmit}>
        <ModalBody>
          {error && <Alert fade={false} color="danger" className="d-flex gap-2"><X size={18} className="flex-shrink-0 mt-1" /><div className="small">{error}</div></Alert>}

          <Row className="g-3">
            <Col md={5}>
              <Label className="small">{tx("bilgiKategorileri.kod")}</Label>
              <Input
                value={form.kod} disabled={!isNew}
                onChange={(e) => setField("kod", e.target.value.toLowerCase())}
                placeholder={tx("bilgiKategorileri.kanGrubu")}
              />
              <div className="form-text small">
                {isNew
                  ? tx("bilgiKategorileri.kucukHarfRakamVeOlusturulduktan")
                  : tx("bilgiKategorileri.kodDegistirilemez")}
              </div>
            </Col>
            <Col md={7}>
              <Label className="small">{tx("bilgiKategorileri.alanAdiEtiket")}</Label>
              <Input value={form.ad} onChange={(e) => setField("ad", e.target.value)} placeholder={tx("bilgiKategorileri.kanGrubu2")} required />
              <div className="form-text small">{tx("bilgiKategorileri.etiketSerbestceDegistirilebilir")}</div>
            </Col>

            <Col md={12}>
              <Label className="small">{tx("bilgiKategorileri.aciklama")}</Label>
              <Input value={form.aciklama || ""} onChange={(e) => setField("aciklama", e.target.value)} placeholder={tx("bilgiKategorileri.acilDurumIcin")} />
            </Col>

            <Col md={5}>
              <Label className="small">{tx("bilgiKategorileri.veriTipi")}</Label>
              <Input
                type="select" value={form.veriTipi} disabled={tipKilitli}
                onChange={(e) => setField("veriTipi", e.target.value)}
              >
                {VERI_TIPLERI.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </Input>
              <div className="form-text small">
                {tipKilitli ? tx("bilgiKategorileri.degerGirilmisBirKategorideVeri") : tipMeta?.hint}
              </div>
            </Col>

            <Col md={3}>
              <Label className="small">{tx("bilgiKategorileri.siralama")}</Label>
              <Input type="number" value={form.siralama} onChange={(e) => setField("siralama", e.target.value)} />
            </Col>

            <Col md={4}>
              <Label className="small d-block">{tx("bilgiKategorileri.kapsam")}</Label>
              <div className="form-check">
                <Input
                  type="checkbox" className="form-check-input" id="fuarBazli" disabled={tipKilitli}
                  checked={form.fuarBazli} onChange={(e) => setField("fuarBazli", e.target.checked)}
                />
                <Label className="form-check-label small" for="fuarBazli">{tx("bilgiKategorileri.fuarBazliFuarBasinaBir")}</Label>
              </div>
              <div className="form-text small">{tx("bilgiKategorileri.kapaliysaKisiBasinaTekDeger")}</div>
            </Col>

            {form.veriTipi === "SECIM" && (
              <Col md={12}>
                <Label className="small">{tx("bilgiKategorileri.secenekler")}</Label>
                <div className="d-flex gap-2 mb-2">
                  <Input
                    bsSize="sm" value={secenekInput} onChange={(e) => setSecenekInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addSecenek(); } }}
                    placeholder={tx("bilgiKategorileri.aRh")}
                  />
                  <Button color="light" size="sm" type="button" onClick={addSecenek}>
                    <ListPlus size={14} className="me-1" />{tx("common:ekle")}
                  </Button>
                </div>
                <div className="d-flex flex-wrap gap-1">
                  {form.secenekler.map((s) => (
                    <Badge key={s} color="light" className="text-body border fw-normal d-inline-flex align-items-center gap-1">
                      {s}
                      <X
                        size={12} style={{ cursor: "pointer" }}
                        onClick={() => setField("secenekler", form.secenekler.filter((x) => x !== s))}
                      />
                    </Badge>
                  ))}
                  {form.secenekler.length === 0 && <span className="text-muted small">{tx("bilgiKategorileri.henuzSecenekYok")}</span>}
                </div>
              </Col>
            )}

            <Col md={6}>
              <div className="form-check">
                <Input
                  type="checkbox" className="form-check-input" id="zorunlu"
                  checked={form.zorunlu} onChange={(e) => setField("zorunlu", e.target.checked)}
                />
                <Label className="form-check-label small" for="zorunlu">{tx("bilgiKategorileri.formdaZorunluOlarakIsaretle")}</Label>
              </div>
              <div className="form-text small">
                {tx("bilgiKategorileri.yalnizcaFormIpucu")}{" "}<strong>{tx("bilgiKategorileri.girisKartiniBloklamaz")}</strong>
              </div>
            </Col>

            <Col md={6}>
              <div className="form-check">
                <Input
                  type="checkbox" className="form-check-input" id="firmaGirebilir"
                  checked={form.firmaGirebilir} onChange={(e) => setField("firmaGirebilir", e.target.checked)}
                />
                <Label className="form-check-label small" for="firmaGirebilir">{tx("bilgiKategorileri.firmaDaGirebilsin")}</Label>
              </div>
              <div className="form-text small">
                {tx("bilgiKategorileri.kapaliysaBuAlaniYalnizcaSaka")}
              </div>
            </Col>
          </Row>

          <Alert fade={false} color="warning" className="mt-3 d-flex gap-2 mb-0">
            <ShieldAlert size={18} className="flex-shrink-0 mt-1" />
            <div className="small">
              <strong>{tx("bilgiKategorileri.kvkkKanunBelgeDegil")}</strong>{" "}{tx("bilgiKategorileri.buradaTanimlananBirSerbestMetin2")}
            </div>
          </Alert>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" type="button" onClick={onClose}>{tx("bilgiKategorileri.vazgec")}</Button>
          <Button color="primary" type="submit" disabled={submitting}>
            {submitting ? <Spinner size="sm" /> : <><Save size={14} className="me-1" />{tx("common:kaydet")}</>}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}

function staffAwareMessage(error) {
  if (isStaffRefusal(error)) {
    return i18n.t("yonetim:bilgiKategorileri.buIslemSakaPersoneliYetkisi") +
      i18n.t("yonetim:bilgiKategorileri.firmaOlarakGorunuyorGrupSatiri");
  }
  return error.message;
}
